import { FC, useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PaymentsApi } from '../api';
import { Button, Loader, PaymentPage } from '../components';
import { RoutePath } from '../config';
import { Text } from '@eo-locale/react';

type PaymentStatus = 'NEW' | 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'EXPIRED' | 'ERROR';

const MAX_TRIES = 5;
const INTERVAL_MS = 5000;

const isFinal = (s: PaymentStatus) =>
  s === 'CONFIRMED' || s === 'REJECTED' || s === 'EXPIRED' || s === 'ERROR';

export const PaymentResultPage: FC = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('OrderID')!;
  const navigate = useNavigate();

  const triesRef = useRef(0);
  const [timedOut, setTimedOut] = useState(false);

  const { data, refetch } = useQuery(
    ['getPaymentStatus', orderId],
    () => PaymentsApi.getPaymentInformation(orderId),
    {
      enabled: Boolean(orderId),
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );

  const status = data?.status as PaymentStatus | undefined;

  // redirect na wynik finalny
  useEffect(() => {
    if (!status) return;
    if (status === 'CONFIRMED') {
      navigate(RoutePath.PaymentSuccessful);
      return;
    }
    if (status === 'REJECTED' || status === 'ERROR' || status === 'EXPIRED') {
      navigate(RoutePath.PaymentFailed);
      return;
    }
  }, [status, navigate]);

  // polling do MAX_TRIES lub do statusu finalnego
  useEffect(() => {
    if (timedOut) return;

    const tick = async () => {
      if (status && isFinal(status)) return; // mamy wynik, nie pytamy dalej
      if (triesRef.current >= MAX_TRIES) {
        setTimedOut(true);
        return;
      }
      triesRef.current += 1;
      await refetch();
    };

    const id = setInterval(tick, INTERVAL_MS);
    // natychmiast pierwsza próba
    tick();

    return () => clearInterval(id);
  }, [status, timedOut, refetch]);

  // "oczekujące" stany to NEW/PENDING lub brak danych przy braku timeoutu
  const pending = status === 'NEW' || status === 'PENDING' || (!status && !timedOut);

  return (
    <PaymentPage title="payment_result:title" subtitle="payment_result:subtitle">
      {pending && !timedOut && <Loader />}

      {timedOut && (
        <div className="flex flex-col items-center gap-4">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-center max-w-xl">
            <p className="text-base mb-1">
              <Text id="payment_result:timeout_title" />
            </p>
            <p className="text-sm text-slate-600">
              <Text id="payment_result:timeout_desc" />
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              theme="secondary"
              onClick={() => {
                // ręczne ponowienie sprawdzenia
                triesRef.current = 0;
                setTimedOut(false);
                refetch();
              }}
            >
              <Text id="payment_result:try_again" />
            </Button>
            <Button onClick={() => navigate(RoutePath.Profile)}>
              <Text id="payment_result:go_to_profile" />
            </Button>
          </div>
        </div>
      )}
    </PaymentPage>
  );
};